<template>
  <div class="task-kanban">
    <!-- Toolbar for column visibility -->
    <table-toolbar
      :show-title="false"
      :show-count="false"
      :searchable="false"
      :show-view-toggle="false"
      :show-column-visibility="true"
      :show-refresh="false"
      :columns="availableFields"
      :visible-columns="internalVisibleFields"
      @column-toggle="handleColumnVisibilityChange"
    />
    
    <div v-if="loading" class="task-kanban__loading">
      Loading tasks...
    </div>

    <template v-else>
      <!-- View mode toggle -->
      <div class="kanban-view-toggle">
        <button
          class="kanban-view-toggle__btn"
          :class="{ 'is-active': kanbanViewMode === 'status' }"
          @click="kanbanViewMode = 'status'"
        >
          By Status
        </button>
        <button
          class="kanban-view-toggle__btn"
          :class="{ 'is-active': kanbanViewMode === 'priority' }"
          @click="kanbanViewMode = 'priority'"
        >
          By Priority
        </button>
      </div>

      <div class="task-kanban__board">
        <div
          v-for="col in activeColumns"
          :key="col.id"
          class="kanban-column"
        >
          <div class="kanban-column__header">
            <h3>{{ col.name }}</h3>
            <span class="kanban-column__count">{{ localLists[col.id] ? localLists[col.id].length : 0 }}</span>
          </div>

        <div class="kanban-column__content">
          <draggable
            v-model="localLists[col.id]"
            :group="'tasks'"
            :animation="200"
            :data-status-id="kanbanViewMode === 'status' ? col.id : undefined"
            :data-priority-id="kanbanViewMode === 'priority' ? col.id : undefined"
            ghost-class="task-card--ghost"
            drag-class="task-card--drag"
            @start="handleDragStart"
            @end="handleDragEnd"
            class="kanban-column__tasks"
          >
            <div
              v-for="task in localLists[col.id]"
              :key="task.id"
              :data-task-id="task.id"
              class="task-card"
              @click="$emit('edit-task', task)"
            >
              <div class="task-card__header">
                <div class="task-card__title-group">
                  <span v-if="task.task_key" class="task-card__key">{{ task.task_key }}</span>
                  <h4>{{ task.title }}</h4>
                </div>
                <span
                  v-if="task.priority && shouldShowField('priority.name')"
                  class="task-badge task-badge--priority"
                  :style="{ backgroundColor: getColorValue(task.priority.color) }"
                >
                  {{ task.priority.name }}
                </span>
              </div>

              <div class="task-card__meta">
                <span v-if="task.assignee && shouldShowField('assignee.name')" class="task-card__assignee">
                  <theme-icon icon="user" type="icons" />
                  {{ task.assignee.name }}
                </span>
                <span v-if="task.start_date && shouldShowField('start_date')" class="task-card__start-date">
                  <theme-icon icon="calendar" type="icons" />
                  Start: {{ formatDate(task.start_date) }}
                </span>
                <span
                  v-if="task.due_date && shouldShowField('due_date')"
                  class="task-card__due-date-pill"
                  :class="getDueDatePillClass(task)"
                >
                  <theme-icon icon="calendar" type="icons" />
                  Due: {{ formatDate(task.due_date) }}
                </span>
                <span v-if="task.project && shouldShowField('project.name')" class="task-card__project">
                  <theme-icon icon="folder" type="icons" />
                  {{ task.project.name }}
                </span>
                <span v-if="task.site && shouldShowField('site.name')" class="task-card__site">
                  <theme-icon icon="map-pin" type="icons" />
                  {{ getSiteDisplayName(task.site) }}
                </span>
              </div>

              <!-- Subtask & Comment indicators -->
              <div class="task-card__indicators" v-if="task.subtask_count > 0 || task.comment_count > 0">
                <span v-if="task.subtask_count > 0" class="task-card__indicator">
                  <theme-icon icon="subtask" type="icons" />
                  {{ task.subtask_count }} {{ task.subtask_count === 1 ? 'subtask' : 'subtasks' }}
                </span>
                <span v-if="task.comment_count > 0" class="task-card__indicator">
                  <theme-icon icon="comment" type="icons" />
                  {{ task.comment_count }} {{ task.comment_count === 1 ? 'comment' : 'comments' }}
                </span>
              </div>
              
              <div v-if="task.description && shouldShowField('description')" class="task-card__description">
                {{ task.description }}
              </div>
            </div>
          </draggable>
        </div>

        <div class="kanban-column__footer">
          <button
            class="kanban-column__add-btn"
            @click="kanbanViewMode === 'status' ? handleCreateTask(col.id) : $emit('create-task', {})"
          >
            <theme-icon icon="plus" type="icons" />
            Add Task
          </button>
        </div>
      </div>
    </div>
    </template>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import draggable from 'vuedraggable';
import ThemeIcon from '../ThemeIcon.vue';
import TableToolbar from '../table/TableToolbar.vue';

export default {
  name: 'TaskKanban',

  components: {
    draggable,
    ThemeIcon,
    TableToolbar
  },

  props: {
    visibleFields: {
      type: Array,
      default: null
      // Array of field names that should be visible in cards
      // If not provided, all fields will be visible
    }
  },

  emits: ['create-task', 'edit-task'],

  data() {
    return {
      localLists: {},
      draggedTask: null,
      isUpdating: false,
      internalVisibleFields: [],
      kanbanViewMode: 'status'
    };
  },

  computed: {
    ...mapState('tasks', ['tasks', 'loading']),
    ...mapGetters('tasks', ['activeStatuses', 'activePriorities', 'tasksByStatus', 'tasksByPriority']),
    
    activeColumns() {
      if (this.kanbanViewMode === 'priority') {
        return this.activePriorities;
      }
      return this.activeStatuses;
    },

    availableFields() {
      // Define all available fields for the column visibility dropdown
      return [
        { field: 'assignee.name', label: 'Assignee' },
        { field: 'priority.name', label: 'Priority' },
        { field: 'start_date', label: 'Start Date' },
        { field: 'due_date', label: 'Due Date' },
        { field: 'project.name', label: 'Project' },
        { field: 'site.name', label: 'Site' },
        { field: 'description', label: 'Description' }
      ];
    },
    
    shouldShowField() {
      return (fieldName) => {
        // Use internal state if available, otherwise fall back to prop
        const fields = this.internalVisibleFields.length > 0 
          ? this.internalVisibleFields 
          : this.visibleFields;
          
        // If no fields specified, show all
        if (!fields || fields.length === 0) {
          return true;
        }
        // Check if field is in visible list
        return fields.includes(fieldName);
      };
    }
  },

  watch: {
    tasks: {
      handler() {
        if (!this.isUpdating) {
          this.syncLocalLists();
        }
      },
      deep: true
    },

    activeStatuses: {
      handler() {
        this.syncLocalLists();
      },
      immediate: true
    },

    activePriorities: {
      handler() {
        if (this.kanbanViewMode === 'priority') {
          this.syncLocalLists();
        }
      },
      immediate: false
    },

    kanbanViewMode() {
      this.syncLocalLists();
    },
    
    visibleFields: {
      handler(newVal) {
        if (newVal && newVal.length > 0) {
          this.internalVisibleFields = [...newVal];
        }
      },
      immediate: true
    }
  },

  async mounted() {
    await this.loadData();
  },

  methods: {
    async loadData() {
      try {
        await this.$store.dispatch('tasks/fetchTasks');
        this.syncLocalLists();
      } catch (error) {
        console.error('Error loading kanban data:', error);
        this.$notify({
          type: 'error',
          title: 'Error',
          text: 'Failed to load tasks. Please try again.'
        });
      }
    },

    syncLocalLists() {
      const lists = {};
      if (this.kanbanViewMode === 'priority') {
        this.activeColumns.forEach(col => {
          lists[col.id] = [...this.tasksByPriority(col.id)];
        });
      } else {
        this.activeStatuses.forEach(status => {
          lists[status.id] = [...this.tasksByStatus(status.id)];
        });
      }
      this.localLists = lists;
    },

    handleDragStart(event) {
      // Capture the task being dragged
      const taskId = parseInt(event.item.getAttribute('data-task-id'));
      this.draggedTask = this.tasks.find(t => t.id === taskId);
    },

    async handleDragEnd(event) {
      const { from, to, oldIndex, newIndex } = event;

      // If dropped in the same column at the same position, do nothing
      if (from === to && oldIndex === newIndex) {
        this.draggedTask = null;
        return;
      }

      const task = this.draggedTask;

      if (!task || !task.id) {
        console.error('Task not found after drag');
        this.syncLocalLists();
        this.draggedTask = null;
        return;
      }

      this.isUpdating = true;

      try {
        if (this.kanbanViewMode === 'priority') {
          const rawPriorityId = to.getAttribute('data-priority-id');
          const toPriorityId = parseInt(rawPriorityId);
          await this.$store.dispatch('tasks/updateTask', {
            id: task.id,
            data: { priority_id: toPriorityId }
          });
        } else {
          const toStatusId = parseInt(to.getAttribute('data-status-id'));
          if (!toStatusId) {
            console.error('Target status ID not found');
            this.syncLocalLists();
            this.draggedTask = null;
            return;
          }
          await this.$store.dispatch('tasks/updateTaskStatus', {
            id: task.id,
            status_id: toStatusId
          });
        }

        this.$notify({
          type: 'success',
          title: 'Success',
          text: 'Task updated successfully'
        });
      } catch (error) {
        console.error('Error updating task:', error);
        this.$notify({
          type: 'error',
          title: 'Error',
          text: 'Failed to update task'
        });
        await this.loadData();
      } finally {
        this.isUpdating = false;
        this.draggedTask = null;
      }
    },

    handleCreateTask(statusId) {
      this.$emit('create-task', { statusId });
    },

    getColorValue(color) {
      const colorMap = {
        blue: '#3b82f6',
        yellow: '#eab308',
        green: '#22c55e',
        red: '#ef4444',
        orange: '#f97316',
        gray: '#6b7280'
      };
      return colorMap[color] || color;
    },

    formatDate(date) {
      if (!date) return '';
      const d = new Date(date);
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    },
    
    getSiteDisplayName(site) {
      if (site.building) {
        if (site.building.building_name) {
          return site.building.building_name;
        }
        // Fall back to address
        const parts = [site.building.address_line_1, site.building.city, site.building.state].filter(Boolean);
        if (parts.length) {
          return parts.join(', ');
        }
      }
      return 'Site #' + site.id;
    },

    getDueDatePillClass(task) {
      if (!task.due_date) return '';
      if (task.is_overdue) return 'task-card__due-date--overdue';
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const due = new Date(task.due_date);
      due.setHours(0, 0, 0, 0);
      const diffDays = Math.ceil((due - today) / (1000 * 60 * 60 * 24));
      if (diffDays <= 3) return 'task-card__due-date--soon';
      return 'task-card__due-date--ok';
    },

    handleColumnVisibilityChange({ field, visible }) {
      // Update internal visible fields array
      if (visible) {
        // Add field if not already in array
        if (!this.internalVisibleFields.includes(field)) {
          this.internalVisibleFields.push(field);
        }
      } else {
        // Remove field from array using splice for Vue reactivity
        const index = this.internalVisibleFields.indexOf(field);
        if (index > -1) {
          this.internalVisibleFields.splice(index, 1);
        }
      }
      
      // Save to localStorage (same key as TaskCards for consistency)
      localStorage.setItem('tasks_table_visible_columns', JSON.stringify(this.internalVisibleFields));
      
      // Emit event to parent if needed
      this.$emit('column-visibility-change', this.internalVisibleFields);
    }
  }
};
</script>
